import type { VRM } from '@pixiv/three-vrm'
import type { AnimationAction, AnimationMixer, Group } from 'three'

import type { useVRMEmote } from '../../composables/vrm/expression'

import { getStageThreeRuntimeTraceContext, isStageThreeRuntimeTraceEnabled } from '../../trace/context'
import { stageThreeTraceVrmCacheEvent } from '../../trace/eventa'

export type IdlePlaybackMode = 'cycle' | 'hold'

export interface ManagedVrmInstance {
  emote: ReturnType<typeof useVRMEmote>
  group: Group
  mixer: AnimationMixer
  modelSrc: string
  scopeKey: string
  vrm: VRM
  // Optional ordered list of AnimationActions used by the idle playlist cycler.
  // Present only when the instance was loaded with an `idleAnimationPlaylist`.
  // Carried through the stash/take cache so the cycle can resume on remount.
  playlistActions?: AnimationAction[]
  idlePlaybackMode?: IdlePlaybackMode
}

// ---------------------------------------------------------------------------
// VRM binary data cache – stores the raw ArrayBuffer (after gzip decompression)
// so that switching A → B → A does not require a second network request.
// ---------------------------------------------------------------------------

interface VrmDataCacheState {
  dataByUrl: Map<string, ArrayBuffer>
}

const MAX_DATA_CACHE_ENTRIES = 20

const hotDataData = import.meta.hot?.data as { vrmDataCacheState?: VrmDataCacheState } | undefined

const vrmDataCacheState = hotDataData?.vrmDataCacheState ?? { dataByUrl: new Map<string, ArrayBuffer>() }

if (import.meta.hot)
  import.meta.hot.data.vrmDataCacheState = vrmDataCacheState

export function getVrmDataCache(modelSrc: string): ArrayBuffer | undefined {
  return vrmDataCacheState.dataByUrl.get(modelSrc)
}

export function setVrmDataCache(modelSrc: string, data: ArrayBuffer): void {
  // Evict oldest entry when cache is full
  if (vrmDataCacheState.dataByUrl.size >= MAX_DATA_CACHE_ENTRIES) {
    const oldestKey = vrmDataCacheState.dataByUrl.keys().next().value
    if (oldestKey !== undefined) {
      vrmDataCacheState.dataByUrl.delete(oldestKey)
    }
  }
  vrmDataCacheState.dataByUrl.set(modelSrc, data)
}

export function clearVrmDataCache(modelSrc?: string): void {
  if (modelSrc) {
    vrmDataCacheState.dataByUrl.delete(modelSrc)
  }
  else {
    vrmDataCacheState.dataByUrl.clear()
  }
}

// ---------------------------------------------------------------------------
// VRM instance cache – stores fully-parsed Three.js VRM instances for
// component remount scenarios (e.g. route change and back).
// ---------------------------------------------------------------------------

interface ManagedVrmCacheState {
  detachedByScope: Record<string, ManagedVrmInstance | undefined>
}

const hotData = import.meta.hot?.data as { managedVrmCacheState?: ManagedVrmCacheState } | undefined

const managedVrmCacheState = hotData?.managedVrmCacheState ?? { detachedByScope: {} }

if (import.meta.hot)
  import.meta.hot.data.managedVrmCacheState = managedVrmCacheState

function emitCacheTrace(action: 'clear' | 'stash' | 'take', scopeKey: string, result: 'empty' | 'evicted' | 'hit' | 'miss' | 'stored', modelSrc?: string) {
  if (!isStageThreeRuntimeTraceEnabled())
    return
  getStageThreeRuntimeTraceContext().emit(stageThreeTraceVrmCacheEvent, {
    action,
    modelSrc,
    result,
    scopeKey,
    ts: performance.now(),
  })
}

export function takeManagedVrmInstance(scopeKey: string, modelSrc: string) {
  const cached = managedVrmCacheState.detachedByScope[scopeKey]
  if (!cached || cached.modelSrc !== modelSrc) {
    emitCacheTrace('take', scopeKey, 'miss', modelSrc)
    return undefined
  }

  delete managedVrmCacheState.detachedByScope[scopeKey]
  emitCacheTrace('take', scopeKey, 'hit', modelSrc)
  return cached
}

export function stashManagedVrmInstance(instance: ManagedVrmInstance) {
  const { scopeKey } = instance
  const previous = managedVrmCacheState.detachedByScope[scopeKey]
  managedVrmCacheState.detachedByScope[scopeKey] = instance

  if (previous === instance) {
    return undefined
  }

  if (previous) {
    emitCacheTrace('stash', scopeKey, 'evicted', instance.modelSrc)
    return previous
  }

  emitCacheTrace('stash', scopeKey, 'stored', instance.modelSrc)
  return undefined
}

export function clearManagedVrmInstance(scopeKey: string) {
  const cached = managedVrmCacheState.detachedByScope[scopeKey]
  delete managedVrmCacheState.detachedByScope[scopeKey]

  if (cached) {
    emitCacheTrace('clear', scopeKey, 'hit', cached.modelSrc)
    return cached
  }

  emitCacheTrace('clear', scopeKey, 'empty')
  return undefined
}
